import type { Academy } from '@court-sync/shared';
import {
  buildAcademyAbsoluteUrl,
  buildAcademyLoginPath,
  buildAcademyPath,
  getAcademyHostMode,
} from '../lib/academyPaths';
import { useAcademySlug } from './useAcademySlug';

type AcademyPathFields = Pick<Academy, 'slug' | 'subdomain' | 'subdomainEnabled' | 'subscriptionTier'>;

function toPathFields(
  academy: Academy | AcademyPathFields | null | undefined,
  slug: string | undefined
): AcademyPathFields | null {
  if (academy) {
    return {
      slug: academy.slug,
      subdomain: academy.subdomain,
      subdomainEnabled: academy.subdomainEnabled,
      subscriptionTier: academy.subscriptionTier,
    };
  }
  if (slug) return { slug };
  return null;
}

/**
 * Subdomain-aware academy path helpers for components in academy public/guardian/coach context.
 */
export function useAcademyPaths(academy?: Academy | AcademyPathFields | null) {
  const slug = useAcademySlug();
  const hostMode = getAcademyHostMode();
  const fields = toPathFields(academy, slug);

  if (!fields) {
    return {
      slug,
      hostMode,
      fields: null as AcademyPathFields | null,
      base: null as string | null,
      p: (_subpath: string) => null as string | null,
      login: (_returnPath?: string) => null as string | null,
      absolute: (_subpath?: string) => null as string | null,
    };
  }

  const base = buildAcademyPath(fields, '', hostMode) || '/';
  const p = (subpath: string) => buildAcademyPath(fields, subpath, hostMode);
  const login = (returnPath?: string) => buildAcademyLoginPath(fields, returnPath, hostMode);
  const absolute = (subpath = '') => buildAcademyAbsoluteUrl(fields, subpath, undefined, hostMode);

  return {
    slug: slug ?? fields.slug,
    hostMode,
    fields,
    base,
    p,
    login,
    absolute,
  };
}
